import { useStore } from '@web/store'
import LocalStorage from '@web/common/LocalStorage'

function toValidUserId (id: unknown): number | null {
  if (typeof id === 'number') return id > 0 ? id : null
  if (typeof id === 'string' && id.trim() !== '') {
    const parsed = Number(id)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }
  return null
}

/**
 * Последний реально увиденный id юзера. Нужен, чтобы clearUserScoped мог
 * почистить ключи, даже если стор в моменте опустел (например словили 401
 * посреди серии await'ов и словили logout, см. finishOnboarding/middleware).
 * Обновляется на каждое успешное чтение id из стора, поэтому при переключении
 * аккаунта (id A -> id B без промежуточного {}) тут же перезаписывается на B
 * и никогда не может «протухнуть» и почистить чужие ключи.
 */
let lastKnownUserId: number | null = null

export function getOnboardingUserId (): number | null {
  const user = useStore().getters['user/user']
  const id = toValidUserId(user?.id)
  if (id !== null) {
    lastKnownUserId = id
  }
  return id
}

/**
 * Тест-утилита: сбрасывает lastKnownUserId, чтобы состояние этого модуля не
 * протекало между тестами/спек-файлами в общем mocha-процессе. Не для
 * использования в продакшн-коде.
 */
export function resetLastKnownUserIdForTests (): void {
  lastKnownUserId = null
}

export function buildUserScopedKey (base: string, userId: number): string {
  return `${base}_${userId}`
}

export function readUserScoped<T> (
  base: string,
  isValid: (value: unknown) => value is T
): T | null {
  const userId = getOnboardingUserId()
  if (userId === null) return null

  const raw = LocalStorage.get(buildUserScopedKey(base, userId))
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    return isValid(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function writeUserScoped (base: string, value: unknown): void {
  const userId = getOnboardingUserId()
  if (userId === null) return
  LocalStorage.set(buildUserScopedKey(base, userId), JSON.stringify(value))
}

export function clearUserScoped (base: string): void {
  // Берём id из стора, а если тот в моменте пуст (например store user
  // обнулился между await'ами из-за logout по 401) — падаем на последний
  // реально увиденный id, чтобы не оставлять ключи в localStorage навсегда.
  const userId = getOnboardingUserId() ?? lastKnownUserId
  if (userId === null) return
  LocalStorage.remove(buildUserScopedKey(base, userId))
}
